import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Wallet,
  Fuel,
  Sun,
  Calendar,
  CreditCard,
  ArrowRight,
  Wrench,
  Umbrella,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { useAutoRefresh } from "../../hooks/useAutoRefresh";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MONTHS_SHORT = ["Sty", "Lut", "Mar", "Kwi", "Maj", "Cze", "Lip", "Sie", "Wrz", "Paź", "Lis", "Gru"];
const MONTHS_FULL = ["Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec", "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"];

const fmtPLN = (n) => Number(n || 0).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " zł";
const fmtDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};

function KpiCard({ label, value, subtitle, Icon, iconBg, iconColor, trend, testId }) {
  return (
    <div className="bg-white border border-border rounded-md p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={testId}>
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-md flex items-center justify-center shrink-0 ${iconBg}`}>
          <Icon className={`w-6 h-6 ${iconColor}`} strokeWidth={2.2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">{label}</div>
          <div className="text-2xl md:text-3xl font-bold text-foreground mt-1 truncate">{value}</div>
          {subtitle && <div className="text-xs text-muted-foreground mt-0.5">{subtitle}</div>}
          {trend !== null && trend !== undefined && (
            <div className={`text-xs font-medium mt-0.5 ${trend >= 0 ? "text-emerald-600" : "text-red-600"}`}>
              {trend >= 0 ? "↑" : "↓"} {Math.abs(trend)}% <span className="text-muted-foreground">vs poprzedni</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionCard({ title, footer, footerTo, children, testId }) {
  return (
    <div className="bg-white border border-border rounded-md p-5 shadow-sm flex flex-col" data-testid={testId}>
      <h3 className="text-[10px] uppercase tracking-[0.12em] font-semibold text-muted-foreground mb-4">{title}</h3>
      <div className="flex-1">{children}</div>
      {footer && (
        <button
          onClick={footerTo}
          className="text-xs font-medium text-blue-600 hover:text-blue-700 mt-4 flex items-center gap-1"
        >
          {footer} <ArrowRight className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

export default function EmployeeHome({ onNavigate, selectedYear, selectedMonth, user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartYear, setChartYear] = useState(selectedYear || new Date().getFullYear());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const y = selectedYear || new Date().getFullYear();
      const m = selectedMonth || new Date().getMonth() + 1;
      const res = await axios.get(`${API}/dashboard/employee-home?year=${y}&month=${m}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      console.error("Employee home load failed", e);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    load();
  }, [load]);

  // Keep home KPIs fresh — admin might publish a new salary while the tab is open.
  useAutoRefresh(load);

  if (loading) {
    return <div className="p-8 text-sm text-muted-foreground" data-testid="emp-home-loading">Ładowanie…</div>;
  }

  const kpi = data?.kpi || {};
  const chart = (data?.salary_chart || []).map((r) => ({ ...r, label: MONTHS_SHORT[r.month - 1] }));
  const terminy = data?.najblizsze_terminy || [];
  const recent = data?.recent_salary || [];

  const urlop = kpi.najblizszy_urlop;
  const urlopValue = urlop ? (urlop.days_left === 0 ? "Dziś" : `Za ${urlop.days_left} dni`) : "Brak";
  const urlopSubtitle = urlop ? fmtDate(urlop.date) : "Brak zaplanowanych";

  const currentMonthName = MONTHS_FULL[(selectedMonth || new Date().getMonth() + 1) - 1];

  return (
    <div className="space-y-4" data-testid="employee-home">
      {/* Period pill */}
      <div className="flex justify-end">
        <div className="inline-flex items-center gap-2 bg-white border border-border rounded-md px-4 py-2 shadow-sm">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">
            {currentMonthName} {selectedYear || new Date().getFullYear()}
          </span>
        </div>
      </div>

      {/* KPI Grid (4 cards) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          testId="emp-kpi-do-wyplaty"
          label="Do wypłaty"
          value={fmtPLN(kpi.do_wyplaty)}
          Icon={Wallet}
          iconBg="bg-emerald-50"
          iconColor="text-emerald-600"
          trend={kpi.do_wyplaty_change_pct}
        />
        <KpiCard
          testId="emp-kpi-paliwo"
          label="Paliwo"
          value={fmtPLN(kpi.paliwo_used)}
          subtitle={`Zużyte w ${currentMonthName.toLowerCase()}`}
          Icon={Fuel}
          iconBg="bg-blue-50"
          iconColor="text-blue-600"
        />
        <KpiCard
          testId="emp-kpi-urlop"
          label="Najbliższy urlop"
          value={urlopValue}
          subtitle={urlopSubtitle}
          Icon={Sun}
          iconBg="bg-amber-50"
          iconColor="text-amber-600"
        />
        <KpiCard
          testId="emp-kpi-raty"
          label="Raty zaległych"
          value={kpi.raty_zaleglych ?? 0}
          subtitle="Moje kredyty"
          Icon={Calendar}
          iconBg="bg-red-50"
          iconColor="text-red-600"
        />
      </div>

      {/* Chart + Terminy */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Salary chart */}
        <div className="bg-white border border-border rounded-md p-5 shadow-sm lg:col-span-2" data-testid="emp-salary-chart">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-[10px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">
              Moje wynagrodzenia w {chartYear}
            </h3>
            <select
              value={chartYear}
              onChange={(e) => setChartYear(Number(e.target.value))}
              className="text-xs border border-border rounded-md px-2 py-1 bg-white"
              data-testid="emp-chart-year-select"
            >
              {Array.from({ length: 2100 - 2024 + 1 }, (_, i) => 2024 + i).map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Suma otrzymanych wypłat</p>
          <div className="h-56">
            <ResponsiveContainer>
              <LineChart data={chart} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <YAxis
                  tick={{ fontSize: 11, fill: "#64748B" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `${Math.round(v / 1000)}k`}
                />
                <Tooltip
                  formatter={(v) => [fmtPLN(v), "Suma"]}
                  labelFormatter={(l, p) => (p && p[0] ? `${MONTHS_FULL[p[0].payload.month - 1]} ${chartYear}` : l)}
                  contentStyle={{ background: "#0F172A", border: "none", borderRadius: 6, color: "#fff", fontSize: 12 }}
                />
                <Line type="monotone" dataKey="total" stroke="#2563EB" strokeWidth={2} dot={{ r: 3, fill: "#2563EB" }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Najbliższe terminy */}
        <SectionCard
          testId="emp-terminy-card"
          title="Najbliższe terminy"
          footer="Zobacz moje pojazdy"
          footerTo={() => onNavigate && onNavigate("pojazdy")}
        >
          {terminy.length === 0 ? (
            <p className="text-sm text-muted-foreground">Brak nadchodzących terminów.</p>
          ) : (
            terminy.slice(0, 4).map((it, i) => {
              const Icon = it.type === "kredyt" ? CreditCard : it.title.includes("Ubezpiecz") ? Umbrella : Wrench;
              return (
                <div key={i} className="flex items-center gap-3 py-2.5 border-b border-border last:border-0">
                  <div className="w-9 h-9 rounded-md flex items-center justify-center shrink-0 bg-blue-50">
                    <Icon className="w-4 h-4 text-blue-600" strokeWidth={2.2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-foreground truncate">{it.title}</div>
                    <div className="text-xs text-muted-foreground truncate">{it.subtitle}</div>
                  </div>
                  <div className="text-right shrink-0 text-xs">
                    <div className="font-semibold text-foreground">{fmtDate(it.date)}</div>
                    <div className={it.days_left <= 7 ? "text-red-600" : "text-blue-600"}>za {it.days_left} dni</div>
                  </div>
                </div>
              );
            })
          )}
        </SectionCard>
      </div>

      {/* Ostatnie wypłaty */}
      <SectionCard
        testId="emp-recent-salary-card"
        title="Ostatnie wypłaty"
        footer="Zobacz wszystkie wypłaty"
        footerTo={() => onNavigate && onNavigate("salary")}
      >
        {recent.length === 0 ? (
          <p className="text-sm text-muted-foreground">Brak wypłat.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {recent.map((r, i) => {
              const period = r.period_start === "01" || r.period_start === "1" ? "01-15" : "16-30";
              return (
                <div key={i} className="border border-border rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    {MONTHS_FULL[r.month - 1]} {r.year} · {period}
                  </div>
                  <div className="mt-1 text-xl font-bold text-emerald-600">{fmtPLN(r.przelew)}</div>
                  <div className="text-xs text-muted-foreground">Dochód: {fmtPLN(r.dochod)}</div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
